'use client'

import { createContext, type PropsWithChildren, useContext, useMemo, useState } from 'react'

import type { ChainId, Token, WithChainId } from '../../web3.types'

interface TokenSelectContextValue {
  search: string
  setSearch: (search: string) => void
  chains: ChainId[]
  selectedChain?: ChainId
  setSelectedChain: (chain: ChainId | undefined) => void
  multiChain: boolean
  filteredTokens: WithChainId<Token>[]
  onTokenSelect: (token: WithChainId<Token>) => void
}

const TokenSelectContext = createContext<TokenSelectContextValue | null>(null)

export function useTokenSelectContext() {
  const context = useContext(TokenSelectContext)
  if (!context) {
    throw new Error('TokenSelect components must be used within a <TokenSelectProvider>')
  }
  return context
}

interface TokenSelectProviderProps<T extends WithChainId<Token>> {
  tokens: T[]
  onTokenSelect: (token: T) => void
  multiChain?: boolean
  initialChainId?: ChainId
  /** Mirrors the search input so the consumer can resolve pasted addresses. */
  onSearchChange?: (search: string) => void
}

export function TokenSelectProvider<T extends WithChainId<Token>>({
  tokens,
  onTokenSelect,
  multiChain = false,
  initialChainId,
  onSearchChange,
  children
}: PropsWithChildren<TokenSelectProviderProps<T>>) {
  const [search, setSearchState] = useState('')
  const setSearch = (nextSearch: string) => {
    setSearchState(nextSearch)
    onSearchChange?.(nextSearch.trim())
  }
  const [selectedChain, setSelectedChain] = useState<ChainId | undefined>(
    multiChain ? initialChainId : undefined
  )

  const chains = useMemo(() => Array.from(new Set(tokens.map(token => token.chainId))), [tokens])

  const filteredTokens = useMemo(() => {
    const lowerCaseSearch = search.trim().toLowerCase()
    return tokens.filter(token => {
      if (multiChain && selectedChain && token.chainId !== selectedChain) return false
      if (
        lowerCaseSearch &&
        !token.name.toLowerCase().includes(lowerCaseSearch) &&
        !token.symbol.toLowerCase().includes(lowerCaseSearch) &&
        !token.address.toLowerCase().includes(lowerCaseSearch)
      ) {
        return false
      }
      return true
    })
  }, [tokens, multiChain, selectedChain, search])

  const value: TokenSelectContextValue = {
    search,
    setSearch,
    chains,
    selectedChain,
    setSelectedChain,
    multiChain,
    filteredTokens,
    onTokenSelect: token => onTokenSelect(token as T)
  }

  return <TokenSelectContext.Provider value={value}>{children}</TokenSelectContext.Provider>
}
